import React from 'react';
import { Box, Typography, CircularProgress, Tooltip, IconButton } from '@mui/material';
import { Autorenew as AutorenewIcon } from '@mui/icons-material';
import { RefreshCw } from 'lucide-react';
import { fsSweep, fsBar, fsSpin } from './animations';

// Token mappings:
// --border → divider
// --bg-subtle → grey.50
// --primary → primary.main
// --primary-soft → rgba(99, 102, 241, 0.09)
// --bg-paper → background.paper
// --divider → divider
// --success → success.main

export interface WorkProductState {
  state: 'idle' | 'generating' | 'ready';
  step?: string;
  /** Live processor percentage (0-100), or null/undefined while unknown. */
  progress?: number | null;
  updated?: string;
  preview?: string;
  artifactId?: string;
}

export interface FeatureStudioCardProps {
  workProduct: {
    id: string;
    name: string;
    desc?: string;
    description?: string;
    needs_rebuild?: boolean;
  };
  state: WorkProductState;
  onGenerate: () => void;
  onOpen: () => void;
  viewMode: 'grid' | 'list';
  /** Removes the list card chrome when the card is nested inside another compact card. */
  embedded?: boolean;
  /** Context for building unique UI bridge IDs (e.g., "product", "feature-123", "userstory-456") */
  context?: string;
  /** Current tab/phase (e.g., "discovery", "design", "code") */
  phase?: string;
  /**
   * Optional handler for the hover-visible "Sync" action. The caller closes
   * over the real feature id (NOT workProduct.id, which is a work-product
   * id) — see FeatureStudioView, which is the level that actually has the
   * selected feature's id in hand.
   */
  onSyncClick?: () => void;
}

function getWorkProductStatusLabel(state: WorkProductState['state']): 'Ready' | 'To Generate' | 'Generating' {
  if (state === 'ready') return 'Ready';
  if (state === 'generating') return 'Generating';
  return 'To Generate';
}

function getWorkProductUiMetadata(workProductName: string, state: WorkProductState['state']): { uiLabel: string; uiDescription: string } {
  const statusLabel = getWorkProductStatusLabel(state);
  const uiLabel = `${workProductName} (${statusLabel})`;

  if (state === 'ready') {
    return {
      uiLabel,
      uiDescription: `${workProductName} is ready. Select to open for editing.`
    };
  }

  if (state === 'generating') {
    return {
      uiLabel,
      uiDescription: `${workProductName} is currently generating.`
    };
  }

  return {
    uiLabel,
    uiDescription: `${workProductName} has not been generated yet. Select to generate it.`
  };
}

export interface WorkProductBridgeMetadata {
  elementId: string;
  uiLabel: string;
  uiRole: 'button';
  uiGroup: string;
  uiDescription: string;
}

/**
 * Canonical metadata consumed by InteligenicUIAdapter/list_ui_elements.
 * Keep every Feature Studio surface on the same context-aware ID contract so
 * the FDE can distinguish product, feature, and user-story work products.
 */
export function getWorkProductBridgeMetadata({
  workProductId,
  workProductName,
  state,
  context = 'product',
  phase = 'discovery',
}: {
  workProductId: string;
  workProductName: string;
  state: WorkProductState['state'];
  context?: string;
  phase?: string;
}): WorkProductBridgeMetadata {
  const { uiLabel, uiDescription } = getWorkProductUiMetadata(workProductName, state);
  return {
    elementId: `wp-${context}-${phase}-${workProductId}`,
    uiLabel,
    uiRole: 'button',
    uiGroup: `workproducts-${phase}`,
    uiDescription,
  };
}

/**
 * FeatureStudioCard - Three-state work product card (idle/generating/ready)
 *
 * States:
 * - IDLE: Dashed border, "Generate" CTA, hover effects
 * - GENERATING: Solid border, spinner, animated sweep/progress bar, step label
 * - READY: Preview text, timestamp, "Open" CTA
 *
 * Supports both grid and list view modes.
 */
export const FeatureStudioCard: React.FC<FeatureStudioCardProps> = ({
  workProduct,
  state,
  onGenerate,
  onOpen,
  viewMode,
  embedded = false,
  context,
  phase,
  onSyncClick,
}) => {
  const [hover, setHover] = React.useState(false);

  const bridge = getWorkProductBridgeMetadata({
    workProductId: workProduct.id,
    workProductName: workProduct.name,
    state: state.state,
    context,
    phase,
  });

  if (viewMode === 'list') {
    return <FeatureStudioCardList
      workProduct={workProduct}
      state={state}
      onGenerate={onGenerate}
      onOpen={onOpen}
      hover={hover}
      setHover={setHover}
      elementId={bridge.elementId}
      onSyncClick={onSyncClick}
      embedded={embedded}
    />;
  }

  // Grid view
  const isIdle = state.state === 'idle';
  const isGenerating = state.state === 'generating';
  const isReady = state.state === 'ready';

  // Clamp to a sane bar width; null keeps the indeterminate sweep.
  const generatingProgress =
    typeof state.progress === 'number' && Number.isFinite(state.progress)
      ? Math.max(0, Math.min(100, Math.round(state.progress)))
      : null;

  const handleClick = () => {
    if (isIdle) {
      onGenerate();
    } else if (isReady) {
      onOpen();
    }
    // generating: no-op
  };
  const actionLabel = isIdle
    ? `Generate ${workProduct.name}`
    : isReady
      ? `Open ${workProduct.name}`
      : `Generating ${workProduct.name}`;
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if ((event.key === 'Enter' || event.key === ' ') && !isGenerating) {
      event.preventDefault();
      handleClick();
    }
  };

  return (
    <Box
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={isGenerating ? -1 : 0}
      aria-label={actionLabel}
      data-ui-bridge={bridge.elementId}
      data-ui-label={bridge.uiLabel}
      data-ui-role={bridge.uiRole}
      data-ui-group={bridge.uiGroup}
      data-ui-description={bridge.uiDescription}
      aria-disabled={isGenerating ? 'true' : 'false'}
      sx={{
        position: 'relative',
        height: '100%',
        minHeight: 180,
        borderRadius: '12px',
        border: isIdle ? '1px dashed' : '1px solid',
        borderColor: isIdle
          ? hover
            ? 'primary.main'
            : 'divider'
          : isGenerating
          ? 'primary.main'
          : 'divider',
        backgroundColor: isIdle
          ? hover
            ? 'rgba(99, 102, 241, 0.04)'
            : 'grey.50'
          : isGenerating
          ? 'rgba(99, 102, 241, 0.09)'
          : 'background.paper',
        cursor: isIdle || isReady ? 'pointer' : isGenerating ? 'progress' : 'default',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        transition: 'all 0.15s',
        overflow: 'hidden',
        boxShadow: isReady && hover ? '0 4px 12px rgba(0, 0, 0, 0.08)' : 'none',
        '&:hover': isReady
          ? {
              borderColor: 'grey.400',
            }
          : {},
      }}
    >
      {/* Top sweep animation (generating only) */}
      {isGenerating && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 2,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              background: 'linear-gradient(90deg, transparent, rgba(99, 102, 241, 0.8), transparent)',
              animation: `${fsSweep} 1.6s linear infinite`,
            }}
          />
        </Box>
      )}

      {/* Sync action — hover-visible, stops propagation so it doesn't open/generate the card */}
      {onSyncClick && hover && (
        <Tooltip title="Sync with Jira/DevOps" placement="top" arrow>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onSyncClick();
            }}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1,
              width: 24,
              height: 24,
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': { backgroundColor: 'grey.100' },
            }}
          >
            <RefreshCw size={13} />
          </IconButton>
        </Tooltip>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        {/* Icon/State indicator */}
        {isGenerating && (
          <CircularProgress
            size={16}
            sx={{
              color: 'primary.main',
              animation: `${fsSpin} 1s linear infinite`,
            }}
          />
        )}
        {isReady && !workProduct.needs_rebuild && (
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '6px',
              backgroundColor: 'rgba(99, 102, 241, 0.12)',
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            📄
          </Box>
        )}
        {isReady && workProduct.needs_rebuild && (
          <Tooltip
            title="Needs rebuild - content may be outdated"
            placement="top"
            arrow
            slotProps={{
              tooltip: {
                sx: {
                  bgcolor: 'common.black',
                  color: 'common.white',
                  fontSize: '0.75rem',
                  '& .MuiTooltip-arrow': {
                    color: 'common.black',
                  },
                },
              },
            }}
          >
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: '6px',
                backgroundColor: 'rgba(239, 108, 0, 0.12)',
                color: '#EF6C00',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AutorenewIcon sx={{ fontSize: 16 }} />
            </Box>
          </Tooltip>
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: isIdle && hover ? 'primary.main' : 'text.primary',
              lineHeight: 1.3,
              marginBottom: isGenerating || isReady ? '4px' : 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {workProduct.name}
          </Typography>

          {/* State eyebrow */}
          {isGenerating && (
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 600,
                color: 'primary.main',
                lineHeight: 1.3,
              }}
            >
              {state.step || 'Starting generation...'}
            </Typography>
          )}
          {isReady && (
            <Typography
              sx={{
                fontSize: 9,
                fontWeight: 800,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'success.main',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              ✓ READY
            </Typography>
          )}
        </Box>
      </Box>

      {/* Body content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {isIdle && (
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 400,
              color: 'text.secondary',
              lineHeight: 1.5,
            }}
          >
            {workProduct.desc || workProduct.description || 'Generate this work product'}
          </Typography>
        )}

        {isGenerating && (
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 400,
              color: 'text.secondary',
              lineHeight: 1.5,
            }}
          >
            {workProduct.desc || workProduct.description || 'Generating work product...'}
          </Typography>
        )}

        {isReady && state.preview && (
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 400,
              color: 'text.secondary',
              lineHeight: 1.5,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {state.preview}
          </Typography>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          paddingTop: '12px',
          borderTop: isReady ? '1px solid' : 'none',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {isIdle && (
          <Typography
            sx={{
              fontSize: 12.5,
              fontWeight: 600,
              color: hover ? 'primary.main' : 'grey.600',
            }}
          >
            + Generate
          </Typography>
        )}

        {isGenerating && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <Box sx={{ position: 'relative', flex: 1, height: 4, backgroundColor: 'rgba(99, 102, 241, 0.2)', borderRadius: 999, overflow: 'hidden' }}>
              {/* Determinate once the processor reports a percentage; the
                  indeterminate sweep covers the queued/unknown window. */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  height: '100%',
                  width: generatingProgress != null ? `${generatingProgress}%` : '40%',
                  backgroundColor: 'primary.main',
                  borderRadius: 999,
                  transition: generatingProgress != null ? 'width 0.3s ease' : 'none',
                  animation: generatingProgress != null ? 'none' : `${fsBar} 1.8s ease-in-out infinite`,
                }}
              />
            </Box>
            {generatingProgress != null && (
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.secondary', minWidth: 26, textAlign: 'right' }}>
                {generatingProgress}%
              </Typography>
            )}
          </Box>
        )}

        {isReady && (
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 500,
              color: 'text.secondary',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            📅 Updated {state.updated} · Open →
          </Typography>
        )}
      </Box>
    </Box>
  );
};

/**
 * List mode rendering
 */
const FeatureStudioCardList: React.FC<{
  workProduct: any;
  state: WorkProductState;
  onGenerate: () => void;
  onOpen: () => void;
  hover: boolean;
  setHover: (h: boolean) => void;
  elementId: string;
  onSyncClick?: () => void;
  embedded?: boolean;
}> = ({ workProduct, state, onGenerate, onOpen, hover, setHover, elementId, onSyncClick, embedded = false }) => {
  const isIdle = state.state === 'idle';
  const isGenerating = state.state === 'generating';
  const isReady = state.state === 'ready';

  const handleClick = () => {
    if (isIdle) onGenerate();
    else if (isReady) onOpen();
  };
  const actionLabel = isIdle
    ? `Generate ${workProduct.name}`
    : isReady
      ? `Open ${workProduct.name}`
      : `Generating ${workProduct.name}`;
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if ((event.key === 'Enter' || event.key === ' ') && !isGenerating) {
      event.preventDefault();
      handleClick();
    }
  };

  // Keep list mode aligned with grid mode for AI list output.
  const { uiLabel, uiDescription } = getWorkProductUiMetadata(workProduct.name, state.state);

  return (
    <Box
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={isGenerating ? -1 : 0}
      aria-label={actionLabel}
      data-ui-bridge={elementId}
      data-ui-label={uiLabel}
      data-ui-role="button"
      data-ui-group="workproducts-list"
      data-ui-description={uiDescription}
      aria-disabled={isGenerating ? 'true' : 'false'}
      sx={{
        position: 'relative',
        padding: embedded ? (isIdle ? '4px 0 22px' : '4px 0 0') : '12px 16px',
        minHeight: embedded && isIdle ? 48 : undefined,
        borderRadius: embedded ? 0 : '8px',
        border: embedded ? 0 : '1px solid',
        borderColor: embedded ? 'transparent' : 'divider',
        backgroundColor: embedded ? 'transparent' : 'background.paper',
        cursor: isIdle || isReady ? 'pointer' : isGenerating ? 'progress' : 'default',
        display: 'flex',
        alignItems: 'center',
        gap: embedded ? 0.75 : 2,
        '&:hover': {
          backgroundColor: embedded ? 'transparent' : 'grey.50',
        },
        overflow: 'hidden',
      }}
    >
      {/* Top sweep (generating only) */}
      {isGenerating && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 2,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              background: 'linear-gradient(90deg, transparent, rgba(99, 102, 241, 0.8), transparent)',
              animation: `${fsSweep} 1.6s linear infinite`,
            }}
          />
        </Box>
      )}

      {/* Rebuild indicator (if needed) */}
      {isReady && workProduct.needs_rebuild && (
        <Tooltip
          title="Needs rebuild - content may be outdated"
          placement="top"
          arrow
          slotProps={{
            tooltip: {
              sx: {
                bgcolor: 'common.black',
                color: 'common.white',
                fontSize: '0.75rem',
                '& .MuiTooltip-arrow': {
                  color: 'common.black',
                },
              },
            },
          }}
        >
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '6px',
              backgroundColor: 'rgba(239, 108, 0, 0.12)',
              color: '#EF6C00',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <AutorenewIcon sx={{ fontSize: 16 }} />
          </Box>
        </Tooltip>
      )}

      {/* State pill */}
      <Box
        sx={{
          padding: embedded ? '3px 7px' : '4px 10px',
          borderRadius: '999px',
          fontSize: embedded ? 9 : 10,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          backgroundColor: isReady
            ? 'rgba(5, 150, 105, 0.1)'
            : isGenerating
            ? 'rgba(99, 102, 241, 0.1)'
            : 'rgba(0, 0, 0, 0.04)',
          color: isReady ? 'success.main' : isGenerating ? 'primary.main' : 'grey.400',
        }}
      >
        {isReady && '✓ Ready'}
        {isGenerating && '⟳ Running'}
        {isIdle && '• Available'}
      </Box>

      {/* Name */}
      <Typography
        sx={{
          flex: 1,
          fontSize: embedded ? 12 : 13,
          fontWeight: 500,
          color: 'text.primary',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {workProduct.name}
      </Typography>

      {/* CTA */}
      {!(embedded && isGenerating) && (
        <Typography
          sx={{
            fontSize: embedded ? 11 : 12,
            fontWeight: 600,
            color: 'primary.main',
            ...(embedded && isIdle
              ? {
                  position: 'absolute',
                  right: 0,
                  bottom: 0,
                  px: 1,
                  py: 0.35,
                  borderRadius: '6px',
                  border: '1px solid',
                  borderColor: 'rgba(99,102,241,.24)',
                  backgroundColor: 'rgba(99,102,241,.07)',
                  lineHeight: 1.2,
                }
              : {}),
          }}
        >
          {isIdle && 'Generate'}
          {isGenerating && 'Running'}
          {isReady && 'Open →'}
        </Typography>
      )}

      {/* Sync action — hover-visible, stops propagation so it doesn't open/generate the row */}
      {onSyncClick && hover && (
        <Tooltip title="Sync with Jira/DevOps" placement="top" arrow>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onSyncClick();
            }}
            sx={{
              width: 24,
              height: 24,
              flexShrink: 0,
              '&:hover': { backgroundColor: 'grey.100' },
            }}
          >
            <RefreshCw size={13} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};
